/**
 * Sampler de path SVG + layout de glyphs sobre la curva, para el Pixi text-on-
 * path (el DOM usa `<textPath>`). Soporta M/L/H/V/Q/C/Z (abs y rel). Devuelve una
 * polilínea densa; si no parsea (o queda <2 puntos) el caller cae a texto plano.
 */

export interface PathPoint {
  x: number;
  y: number;
}

export interface GlyphPlacement {
  ch: string;
  x: number;
  y: number;
  angle: number;
}

const pushLine = (
  out: PathPoint[],
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  steps: number,
) => {
  for (let s = 1; s <= steps; s += 1) {
    const t = s / steps;
    out.push({ x: x0 + (x1 - x0) * t, y: y0 + (y1 - y0) * t });
  }
};

const pushQuad = (
  out: PathPoint[],
  x0: number,
  y0: number,
  cx: number,
  cy: number,
  x1: number,
  y1: number,
  steps: number,
) => {
  for (let s = 1; s <= steps; s += 1) {
    const t = s / steps;
    const mt = 1 - t;
    out.push({
      x: mt * mt * x0 + 2 * mt * t * cx + t * t * x1,
      y: mt * mt * y0 + 2 * mt * t * cy + t * t * y1,
    });
  }
};

const pushCubic = (
  out: PathPoint[],
  x0: number,
  y0: number,
  c1x: number,
  c1y: number,
  c2x: number,
  c2y: number,
  x1: number,
  y1: number,
  steps: number,
) => {
  for (let s = 1; s <= steps; s += 1) {
    const t = s / steps;
    const mt = 1 - t;
    out.push({
      x:
        mt * mt * mt * x0 +
        3 * mt * mt * t * c1x +
        3 * mt * t * t * c2x +
        t * t * t * x1,
      y:
        mt * mt * mt * y0 +
        3 * mt * mt * t * c1y +
        3 * mt * t * t * c2y +
        t * t * t * y1,
    });
  }
};

export const sampleSvgPath = (d: string, steps = 24): PathPoint[] => {
  const tokens = d.match(/[a-zA-Z]|-?\d*\.?\d+(?:e-?\d+)?/g);
  if (!tokens) return [];
  let i = 0;
  const out: PathPoint[] = [];
  let cx = 0;
  let cy = 0;
  let startX = 0;
  let startY = 0;
  let cmd = "";
  const num = () => parseFloat(tokens[i++]);
  const isCmd = (t: string) => /^[a-zA-Z]$/.test(t);

  while (i < tokens.length) {
    if (isCmd(tokens[i])) cmd = tokens[i++];
    if (i > tokens.length) break;
    const rel = cmd === cmd.toLowerCase();
    const C = cmd.toUpperCase();

    if (C === "M") {
      let x = num();
      let y = num();
      if (rel) {
        x += cx;
        y += cy;
      }
      cx = x;
      cy = y;
      startX = x;
      startY = y;
      out.push({ x: cx, y: cy });
      cmd = rel ? "l" : "L"; // pares siguientes = lineto
    } else if (C === "L") {
      let x = num();
      let y = num();
      if (rel) {
        x += cx;
        y += cy;
      }
      pushLine(out, cx, cy, x, y, steps);
      cx = x;
      cy = y;
    } else if (C === "H") {
      let x = num();
      if (rel) x += cx;
      pushLine(out, cx, cy, x, cy, steps);
      cx = x;
    } else if (C === "V") {
      let y = num();
      if (rel) y += cy;
      pushLine(out, cx, cy, cx, y, steps);
      cy = y;
    } else if (C === "Q") {
      let x1 = num();
      let y1 = num();
      let x = num();
      let y = num();
      if (rel) {
        x1 += cx;
        y1 += cy;
        x += cx;
        y += cy;
      }
      pushQuad(out, cx, cy, x1, y1, x, y, steps);
      cx = x;
      cy = y;
    } else if (C === "C") {
      let x1 = num();
      let y1 = num();
      let x2 = num();
      let y2 = num();
      let x = num();
      let y = num();
      if (rel) {
        x1 += cx;
        y1 += cy;
        x2 += cx;
        y2 += cy;
        x += cx;
        y += cy;
      }
      pushCubic(out, cx, cy, x1, y1, x2, y2, x, y, steps);
      cx = x;
      cy = y;
    } else if (C === "Z") {
      pushLine(out, cx, cy, startX, startY, steps);
      cx = startX;
      cy = startY;
    } else {
      i += 1; // comando no soportado → saltear token
    }
  }
  return out;
};

/**
 * Coloca cada caracter de `chars` sobre la polilínea, separados por `advance` px,
 * arrancando en `startOffsetPct`% del largo total. Devuelve posición + ángulo
 * tangente por glyph. `null` si la polilínea es inválida.
 */
export const layoutTextOnPath = (
  points: PathPoint[],
  chars: string[],
  advance: number,
  startOffsetPct: number,
): GlyphPlacement[] | null => {
  if (points.length < 2) return null;

  const cum: number[] = [0];
  for (let k = 1; k < points.length; k += 1) {
    const dx = points[k].x - points[k - 1].x;
    const dy = points[k].y - points[k - 1].y;
    cum.push(cum[k - 1] + Math.hypot(dx, dy));
  }
  const total = cum[cum.length - 1];
  if (total <= 0) return null;

  const pointAt = (dist: number): GlyphPlacement => {
    const d = Math.max(0, Math.min(total, dist));
    let k = 1;
    while (k < cum.length && cum[k] < d) k += 1;
    const a = points[k - 1];
    const b = points[Math.min(k, points.length - 1)];
    const segLen = cum[k] - cum[k - 1] || 1;
    const t = (d - cum[k - 1]) / segLen;
    return {
      ch: "",
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      angle: Math.atan2(b.y - a.y, b.x - a.x),
    };
  };

  const out: GlyphPlacement[] = [];
  let dist = (startOffsetPct / 100) * total + advance / 2;
  for (const ch of chars) {
    const p = pointAt(dist);
    out.push({ ...p, ch });
    dist += advance;
  }
  return out;
};
